import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

export function Field({
  label,
  hint,
  error,
  children,
  optional,
  id,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | undefined;
  children: (props: { id: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) => ReactNode;
  optional?: boolean;
  id?: string;
}) {
  const auto = useId();
  const fid = id ?? auto;
  const describedBy =
    [hint ? `${fid}-hint` : '', error ? `${fid}-err` : ''].filter(Boolean).join(' ') || undefined;
  return (
    <div className="field">
      <label htmlFor={fid}>
        {label}
        {optional && (
          <span className="muted" style={{ fontWeight: 400 }}>
            {' '}
            · Optional
          </span>
        )}
      </label>
      {children({
        id: fid,
        ...(error ? { 'aria-invalid': true } : {}),
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
      })}
      {hint && (
        <span className="hint" id={`${fid}-hint`}>
          {hint}
        </span>
      )}
      {error && (
        <span className="error-text" id={`${fid}-err`}>
          {error}
        </span>
      )}
    </div>
  );
}

export const Input = (p: InputHTMLAttributes<HTMLInputElement>) => (
  <input className={`input ${p.className ?? ''}`} {...p} />
);
export const Select = ({ children, ...p }: SelectHTMLAttributes<HTMLSelectElement>) => (
  <select className={`select ${p.className ?? ''}`} {...p}>
    {children}
  </select>
);
export const Textarea = ({
  mono,
  ...p
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { mono?: boolean; ref?: Ref<HTMLTextAreaElement> }) => (
  <textarea className={`textarea ${mono ? 'mono-text' : ''} ${p.className ?? ''}`} {...p} />
);

export function Checkbox({
  label,
  checked,
  onChange,
  disabled,
  description,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  description?: ReactNode;
}) {
  return (
    <label className="check">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="stack" style={{ gap: 2 }}>
        <span>{label}</span>
        {description && <span className="small muted">{description}</span>}
      </span>
    </label>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="form-grid">
      <div className="section-label">
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
      <div className="stack">{children}</div>
    </section>
  );
}

/** Editable list of short strings (acceptance criteria, checks). */
export function ListEditor({
  items,
  onChange,
  placeholder,
  addLabel,
  label,
}: {
  items: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  addLabel: string;
  label: string;
}) {
  return (
    <div className="stack sm" role="group" aria-label={label}>
      {items.map((item, i) => (
        <div key={i} className="row nowrap">
          <span className="mono muted" style={{ width: 20 }}>
            {i + 1}.
          </span>
          <input
            className="input"
            value={item}
            aria-label={`${label} ${i + 1}`}
            placeholder={placeholder}
            onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))}
          />
          <button
            type="button"
            className="btn btn-ghost sm"
            onClick={() => onChange(items.filter((_, j) => j !== i))}
            aria-label={`Remove ${label.toLowerCase()} ${i + 1}`}
          >
            Remove
          </button>
        </div>
      ))}
      <div>
        <button type="button" className="btn btn-secondary sm" onClick={() => onChange([...items, ''])}>
          {addLabel}
        </button>
      </div>
    </div>
  );
}
